import dayjs from 'dayjs';

import { QUARANTINE_TYPE_NAME_MAP } from './constants';
import {
  QuarantineLogContentReqDto,
  QuarantineLogFacetReqDto,
  QuarantineLogState,
} from './types';
import { QuarantineLogTypeEnum } from '../../../types/log';
import { SERVICE_NAME_MAP } from '../../../utils/service';

/**
 * @description check quarantine item can delete/store/download or not
 */
export const checkQuarantineItemActionEnabled = (
  quarantineTypeKey: string,
  fileStatusKey: string
) => {
  if (quarantineTypeKey === 'TempQuarantine') {
    if (
      [
        'Deleting',
        'Restoring',
        'QuarantineFailure',
        'Quarantining',
        'Quarantined',
        'DeleteSuccess',
        'RestoreSuccess',
      ].indexOf(fileStatusKey) >= 0
    ) {
      return false;
    }
  } else if (
    ['Deleting', 'Restoring', 'QuarantineFailure'].indexOf(fileStatusKey) >= 0
  ) {
    return false;
  }
  return true;
};

export const constructorQuarantineContentAndFacetApiParams = (
  state: QuarantineLogState
): [QuarantineLogContentReqDto, QuarantineLogFacetReqDto] => {
  const { quarantineType, page, sort, timeRange, facets } = state;
  const serviceName = QUARANTINE_TYPE_NAME_MAP.get(quarantineType) as string;
  const contentListParams: QuarantineLogContentReqDto = {
    serviceName,
    page,
    sort,
    timeRange,
    searches: JSON.stringify([...facets]),
  };
  const facetsParams: QuarantineLogFacetReqDto = {
    serviceName,
    timeRange,
    searches: JSON.stringify([...facets]),
  };
  return [contentListParams, facetsParams];
};

export const getCustomizedName = function (fileName: string, violator: string) {
  let tempFileName = fileName ? '_' + fileName : '';
  const tempViolator = violator
    ? '_' + violator.substring(0, violator.lastIndexOf('@'))
    : '';
  if (tempFileName !== '' && tempFileName.length > 127) {
    tempFileName = tempFileName.substring(0, tempFileName.length / 2);
  }
  return dayjs().format('YYYYMMDDHHmm') + tempFileName + tempViolator + '.zip';
};

export const base64ToString = (downloadInfo: string) => {
  let downloadInfoString = '';
  try {
    downloadInfoString = decodeURIComponent(
      atob(downloadInfo)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
  } catch (err) {
    throw new Error('UNABLE_TO_DOWNLOAD');
    // $scope.btn_status.download = false;
    // ErrorGlobal.set(
    //   'ERRORS.UNABLE_TO_DOWNLOAD',
    //   null,
    //   'quarantine_error_message_info'
    // );
  }
  return downloadInfoString;
};

export const getQuarantineDeleteActionURL = (
  quarantineType: QuarantineLogTypeEnum
) => {
  switch (quarantineType) {
    case QuarantineLogTypeEnum.SharePoint:
    case QuarantineLogTypeEnum.OneDrive:
      return 'spod/delete';
    case QuarantineLogTypeEnum.Exchange:
      return 'exchangeOnline/delete';
    case QuarantineLogTypeEnum.Box:
    case QuarantineLogTypeEnum.Dropbox:
    case QuarantineLogTypeEnum.GoogleDrive:
    case QuarantineLogTypeEnum.SalesforceProduction:
    case QuarantineLogTypeEnum.SalesforceSandbox:
      return 'cloudApp/delete';
    case QuarantineLogTypeEnum.Teams:
      return 'teams/delete';
    case QuarantineLogTypeEnum.Gmail:
      return 'gmail/delete';
    case QuarantineLogTypeEnum.ExchangeServer:
      return 'exchangeServer/delete';
    case QuarantineLogTypeEnum.ExchangeInline:
    case QuarantineLogTypeEnum.GmailInline:
      // inline services can not delete
      return '';
  }
};

export const getQuarantineRestoreActionURL = (
  quarantineType: QuarantineLogTypeEnum
) => {
  switch (quarantineType) {
    case QuarantineLogTypeEnum.SharePoint:
    case QuarantineLogTypeEnum.OneDrive:
      return 'spod/restore';
    case QuarantineLogTypeEnum.Exchange:
      return 'exchangeOnline/restore';
    case QuarantineLogTypeEnum.Box:
    case QuarantineLogTypeEnum.Dropbox:
    case QuarantineLogTypeEnum.GoogleDrive:
    case QuarantineLogTypeEnum.SalesforceProduction:
    case QuarantineLogTypeEnum.SalesforceSandbox:
      return 'cloudApp/restore';
    case QuarantineLogTypeEnum.Teams:
      return 'teams/restore';
    case QuarantineLogTypeEnum.Gmail:
      return 'gmail/restore';
    case QuarantineLogTypeEnum.ExchangeServer:
      return 'exchangeServer/restore';
    case QuarantineLogTypeEnum.ExchangeInline:
    case QuarantineLogTypeEnum.GmailInline:
      return 'inlineMail/restore';
  }
};

export const getQuarantineDownloadActionURL = (
  quarantineType: QuarantineLogTypeEnum
) => {
  switch (quarantineType) {
    case QuarantineLogTypeEnum.SharePoint:
    case QuarantineLogTypeEnum.OneDrive:
      return 'spod/download';
    case QuarantineLogTypeEnum.Exchange:
      return 'exchangeOnline/download';
    case QuarantineLogTypeEnum.Box:
    case QuarantineLogTypeEnum.Dropbox:
    case QuarantineLogTypeEnum.GoogleDrive:
    case QuarantineLogTypeEnum.SalesforceProduction:
    case QuarantineLogTypeEnum.SalesforceSandbox:
      return 'cloudApp/createLink';
    case QuarantineLogTypeEnum.Teams:
      return 'teams/download';
    case QuarantineLogTypeEnum.Gmail:
      return 'gmail/download';
    case QuarantineLogTypeEnum.ExchangeInline:
    case QuarantineLogTypeEnum.GmailInline:
      return 'inlineMail/download';
    case QuarantineLogTypeEnum.ExchangeServer: //ExchangeServer can not download
      return '';
  }
};

export const convertScanSourceToQuarantineType = (
  scanSource: string | null
): QuarantineLogTypeEnum | null => {
  if (
    scanSource &&
    SERVICE_NAME_MAP.get(scanSource) &&
    scanSource !== 'teams_chat'
  ) {
    return SERVICE_NAME_MAP.get(scanSource) as unknown as QuarantineLogTypeEnum;
  }
  return null;
};
